import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';

interface FilterDropdownProps {
  data: any[];
  field: string;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  /** Optional: use a different field for extracting filter values (for columns where display differs from raw data) */
  filterField?: string;
  /** Optional: custom function to extract the filter display value from a row */
  filterValue?: (rowData: any) => string;
}

const SEARCH_THRESHOLD = 8; // Show search if more than this many values

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  data,
  field,
  value,
  onChange,
  placeholder = 'Filter...',
  filterField,
  filterValue: filterValueFn,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Resolve which field to read for filter values
  const resolvedField = filterField || field;

  // Extract unique values from data using useMemo to prevent infinite loops
  const uniqueValues = useMemo(() => {
    return data
      .map(item => {
        if (filterValueFn) return filterValueFn(item);
        const fieldValue = item[resolvedField];
        if (fieldValue === null || fieldValue === undefined) return '';
        return String(fieldValue);
      })
      .filter((val, index, arr) => arr.indexOf(val) === index)
      .sort();
  }, [data, resolvedField, filterValueFn]);

  // Filter values based on search text
  const filteredValues = useMemo(() => {
    if (!searchText.trim()) return uniqueValues;
    const search = searchText.toLowerCase();
    return uniqueValues.filter(val => val.toLowerCase().includes(search));
  }, [uniqueValues, searchText]);

  const showSearch = uniqueValues.length > SEARCH_THRESHOLD;

  // Update dropdown position when opened and focus search
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX
      });
      // Focus search input after a brief delay
      if (showSearch) {
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
    } else {
      // Reset search when closing
      setSearchText('');
    }
  }, [isOpen, showSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current && !dropdownRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleValueToggle = (val: string) => {
    const newValue = value.includes(val)
      ? value.filter(v => v !== val)
      : [...value, val];
    onChange(newValue);
  };

  const handleSelectAll = () => {
    onChange(uniqueValues);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const getDisplayText = () => {
    if (value.length === 0) return 'All';
    if (value.length === 1) return value[0];
    if (value.length === uniqueValues.length) return 'All';
    return `${value.length} selected`;
  };

  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      className="fixed w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-80 overflow-hidden"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        zIndex: 9999
      }}
    >
      {/* Header with controls */}
      <div className="p-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
        <div className="flex justify-between items-center">
          <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Filter Options</span>
          <div className="flex gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleSelectAll();
              }}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30"
            >
              All
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
              className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              None
            </button>
          </div>
        </div>
      </div>

      {/* Search input - only show if many values */}
      {showSearch && (
        <div className="p-2 border-b border-gray-100 dark:border-gray-700">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search..."
              className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
              onClick={(e) => e.stopPropagation()}
            />
            <i className="ri-search-line absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-3 h-3"></i>
            {searchText && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchText('');
                }}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              >
                <i className="ri-close-line w-3 h-3"></i>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Values list */}
      <div className="max-h-48 overflow-y-auto">
        {filteredValues.map((val, index) => (
          <label
            key={index}
            className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-900 dark:text-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={value.includes(val)}
              onChange={() => handleValueToggle(val)}
              className="mr-2 h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
            />
            <span className="truncate flex-1" title={val}>
              {val || '(Empty)'}
            </span>
          </label>
        ))}
      </div>

      {filteredValues.length === 0 && (
        <div className="p-3 text-center text-gray-500 dark:text-gray-400 text-sm">
          {searchText ? 'No matching values' : 'No values found'}
        </div>
      )}
    </div>
  );

  return (
    <div className="relative w-full">
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="flex items-center justify-between w-full px-1 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent overflow-hidden text-gray-900 dark:text-gray-100"
      >
        <span className="truncate flex-1 text-left min-w-0">
          {getDisplayText()}
        </span>
        <i className={`ri-filter-line ml-1 w-3 h-3 flex-shrink-0 ${value.length > 0 && value.length < uniqueValues.length ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}></i>
      </button>

      {typeof document !== 'undefined' && createPortal(dropdownContent, document.body)}
    </div>
  );
};
